import React, { useState } from 'react';
import { Calendar, Info, Settings } from 'lucide-react';
import SettingsModal from './SettingsModal';
import StatusToast from './StatusToast';
import UserGuideModal from './UserGuideModal';
import { formatSignedInAs } from '../utils/formatting';

// Extracted from App.js (App.js modularization, Sep 2026) — the
// step === 'welcome' screen: the login/magic-link gate for a signed-out
// visitor, and also (less obviously) the screen a GA lands back on after
// deleting their last remaining calendar while still signed in
// (handleDeleteCalendar calls setStep('welcome') directly).
//
// Bug found + fixed during this extraction (5 Sep): same class of issue as
// create-calendar — the authenticated header's Settings button called
// setShowSettings(true) but no <SettingsModal> was ever mounted in this
// early-return block, so it silently did nothing. Unrestricted tabs here
// too, matching create-calendar (this authenticated state is a GA/SA
// account, not a plain member).
const WelcomeStep = ({
  statusMessage,
  hasAuthToken,
  magicLinkSent,
  setMagicLinkSent,
  signedInElsewhere,
  loginEmail,
  setLoginEmail,
  savedEmails,
  onRemoveSavedEmail,
  onSendMagicLink,
  isSendingMagicLink,
  showUserGuide,
  onOpenUserGuide,
  onCloseUserGuide,
  isAuthenticated,
  onSignOut,
  showSettings,
  setShowSettings,
  settingsInitialTab,
  setSettingsInitialTab,
  userEmail,
  apiBaseUrl,
  authToken,
  onStatusMessage,
  userTier,
  firstName,
  lastName,
  preferredName,
  isSA,
  raRegions,
  onSaveProfile,
}) => {
  // (26 Sep 2026) The app's own list of addresses used to sign in on this
  // device, drawn by the app so it looks the same in every browser. The
  // browsers' own suggestions are switched off for this box (autoComplete
  // "off"; Firefox honours it fully, Edge/Chrome mostly), replacing the old
  // datalist that Edge merged with its own autofill.
  const [showSaved, setShowSaved] = useState(false);
  const clearLoginEmail = () => {
    setLoginEmail('');
    setShowSaved(true);
    const input = document.getElementById('login-email-input');
    if (input) input.focus();
  };
  const typed = (loginEmail || '').trim().toLowerCase();
  const savedToShow = (savedEmails || []).filter((e) =>
    !typed || savedEmails.some((s) => s === typed) || e.includes(typed));
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Toast Notification - Centered */}
      <StatusToast statusMessage={statusMessage} />

      {/* LOGIN MODAL - Shows when not authenticated */}
      {!hasAuthToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-2">
            <div className="text-center mb-6">
              <img src="/Calendar_Hub_Logo.png" alt="Calendar Hub" className="w-24 h-24 mx-auto mb-4 object-contain" />
              <h2 className="text-2xl font-bold text-gray-900">Welcome to the Telematrix Calendar Hub</h2>
              <p className="text-gray-600 mt-2">Sign in to manage your calendars</p>
            </div>

            {/* (11 Oct 2026, 8.4, decision 37) Another tab of this browser signed in
                from the email's link, and this tab was not the one to take over. */}
            {magicLinkSent && signedInElsewhere ? (
              <div className="text-center">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium">✓ You're signed in in another tab.</p>
                  <p className="text-gray-600 text-sm mt-2">You can close this one.</p>
                </div>
              </div>
            ) : !magicLinkSent ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative mb-2">
                  <input
                    id="login-email-input"
                    name="calendarhub-sign-in"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => { setLoginEmail(e.target.value); setShowSaved(true); }}
                    onFocus={() => setShowSaved(true)}
                    // (26 Sep 2026) Closed a moment later, not at once, so a tap on an
                    // entry still registers on phones where the box loses focus first.
                    onBlur={() => setTimeout(() => setShowSaved(false), 200)}
                    placeholder="your.email@example.com"
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && onSendMagicLink()}
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  {loginEmail && (
                    <button
                      type="button"
                      // (26 Sep 2026) This × sits on top of the text box, and on iPhone a
                      // tap there could go to the box instead of this button. Acting on
                      // the touch itself (and cancelling the click that would follow)
                      // makes it respond on iPhone; onClick still serves mouse users.
                      onTouchEnd={(e) => { e.preventDefault(); clearLoginEmail(); }}
                      onClick={clearLoginEmail}
                      className="absolute right-1 top-1/2 -translate-y-1/2 z-20 px-2 py-1 text-gray-500 hover:text-gray-800 text-xl leading-none"
                      aria-label="Clear email"
                    >
                      ×
                    </button>
                  )}
                  {/* (26 Sep 2026) Addresses used to sign in on this device. No
                      mousedown tricks here: iPhone Safari could swallow the tap
                      (the clear × stopped working). The box's onBlur closes the
                      list only after a short delay, so taps still register. */}
                  {showSaved && savedToShow.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden text-left">
                      <p className="px-3 pt-2 pb-1 text-xs text-gray-500">Used to sign in on this device</p>
                      <ul className="max-h-60 overflow-y-auto">
                        {savedToShow.map((email) => (
                          <li key={email} className="flex items-center border-t border-gray-100">
                            <button
                              type="button"
                              onClick={() => { setLoginEmail(email); setShowSaved(false); }}
                              className="flex-1 min-w-0 text-left px-3 py-2.5 text-sm text-gray-800 hover:bg-blue-50 break-all"
                            >
                              {email}
                            </button>
                            {onRemoveSavedEmail && (
                              <button
                                type="button"
                                onClick={() => onRemoveSavedEmail(email)}
                                className="px-3 py-2.5 text-gray-500 hover:text-red-600 text-lg leading-none"
                                aria-label={`Forget ${email} on this device`}
                              >
                                ×
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <button
                  onClick={onSendMagicLink}
                  disabled={isSendingMagicLink || !loginEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSendingMagicLink ? 'Sending...' : 'Send email'}
                </button>
                <p className="text-sm text-gray-500 mt-4 text-center">
                  We'll send you a secure link to sign in
                </p>
                <div className="mt-4 text-center">
                  <button
                    onClick={onOpenUserGuide}
                    className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 text-sm font-medium mx-auto"
                  >
                    <Info className="w-4 h-4" />
                    Guide
                  </button>
                </div>
              </div>
        ) : (
              <div className="text-center">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800 font-medium">✓ Email sent!</p>
                  <p className="text-green-600 text-sm mt-1">Check your email and click the sign-in link</p>
                  <p className="text-gray-600 text-sm mt-2 italic">You can close this tab now</p>
                </div>

                <div>
                  <button
                    onClick={() => {
                      setMagicLinkSent(false);
                      setLoginEmail('');
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* HEADER - Always show when authenticated */}
      {hasAuthToken && (
        <div className="bg-white shadow-sm border-b sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Telematrix Calendar Hub</h1>
                  <p className="text-xs text-gray-500">Create your first calendar to get started</p>
                  {userEmail && (
                    <p className="text-xs text-gray-400">
                      Signed in as: {formatSignedInAs(preferredName, firstName, lastName, userEmail)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {isAuthenticated && (
                  <button
                    onClick={onSignOut}
                    className="px-2 py-1.5 text-xs text-red-600 hover:text-red-700 font-medium border border-red-300 rounded-lg hover:bg-red-50 transition whitespace-nowrap"
                  >
                    Sign Out
                  </button>
                )}
                <button
                  onClick={onOpenUserGuide}
                  className="flex items-center gap-1 text-gray-700 hover:text-blue-600 px-2 py-1.5 text-xs font-medium transition"
                  title="User Guide"
                >
                  <Info className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex items-center gap-1 px-2 py-1.5 text-gray-600 hover:text-gray-900 text-xs whitespace-nowrap"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* WELCOME SCREEN CONTENT — background behind login modal */}
      <div className="flex items-center justify-center p-4" style={{ minHeight: 'calc(100vh - 80px)' }}>
        <div className="text-center">
          <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-10 h-10 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Telematrix Calendar Hub</h1>
          <p className="text-gray-500 text-sm">Sign in to manage your calendars</p>
        </div>
      </div>

      {/* USER GUIDE MODAL - must be inside welcome return */}
      <UserGuideModal
        isOpen={showUserGuide}
        onClose={onCloseUserGuide}
        isOnDashboard={false}
        userPreferredCurrency={null}
      />

      {showSettings && (
        <SettingsModal
          isOpen={showSettings}
          onClose={() => { setShowSettings(false); setSettingsInitialTab('profile'); }}
          userEmail={userEmail}
          apiBaseUrl={apiBaseUrl}
          authToken={authToken}
          onStatusMessage={onStatusMessage}
          userTier={userTier}
          initialTab={settingsInitialTab}
          currentCalendar={null}
          firstName={firstName}
          lastName={lastName}
          isSA={isSA}
          raRegions={raRegions}
          onSaveProfile={onSaveProfile}
          restrictToProfileTabs={false}
        />
      )}
    </div>
  );
};

export default WelcomeStep;
